/**
 * Business-level permissions for RBAC.
 * Each permission represents an elevated business action beyond baseline access.
 *
 * Baseline actions (implicit for all authenticated users, no permission needed):
 * - View invoices (create/import stay baseline; edit and delete need invoices.edit)
 * - View documents
 * - View parties
 * - View cost centers
 * - View organizational structure
 * - Record, confirm and archive a goods receipt — ordinary warehouse work
 */
export const Permission = {
  // Invoices — elevated actions only
  INVOICES_EDIT: 'invoices.edit',
  INVOICES_APPROVE: 'invoices.approve',
  INVOICES_EXPORT: 'invoices.export',
  INVOICES_MATCH: 'invoices.match',

  // Requisitions — elevated actions only
  REQUISITIONS_APPROVE: 'requisitions.approve',

  // Approval rounds — changing who is asked, on any document kind
  APPROVALS_MANAGE: 'approvals.manage',

  // Purchase orders — elevated actions only
  PURCHASE_ORDERS_APPROVE: 'purchase_orders.approve',

  // Contracts — create, edit, renew and terminate
  CONTRACTS_MANAGE: 'contracts.manage',

  // Parties — elevated actions
  PARTIES_MANAGE: 'parties.manage',

  // Payee bank accounts — sign, reject or revoke an account's verification
  BANK_ACCOUNTS_VERIFY: 'bank_accounts.verify',

  // Payments — reconciliation, ERP document link decisions, confirming a run by hand and
  // revoking a confirmation
  PAYMENTS_RECONCILE: 'payments.reconcile',
  // Payment runs — build, edit and cancel a proposal; block a payment
  PAYMENTS_PREPARE: 'payments.prepare',
  // Payment runs — approve, unblock after approval, cancel an approved run
  PAYMENTS_APPROVE: 'payments.approve',
  // Payment runs — release, export and its download, withdraw, cancel a released run
  PAYMENTS_RELEASE: 'payments.release',
  // Payments — record a payment on an invoice ("Record payment")
  PAYMENTS_RECORD: 'payments.record',
  // Bank statements — upload a statement of one of the account's own bank accounts
  BANK_STATEMENTS_IMPORT: 'bank_statements.import',

  // Users & Account
  USERS_MANAGE: 'users.manage',
  ACCOUNT_SETTINGS: 'account.settings',

  // Analysis & Approval rules
  RULES_VIEW: 'rules.view',
  RULES_MANAGE: 'rules.manage',

  // Cost centers — elevated actions
  COST_CENTERS_MANAGE: 'cost_centers.manage',

  // Jobs (zakázky) — elevated actions
  JOBS_MANAGE: 'jobs.manage',

  // Organizational structure — elevated actions
  ORG_UNITS_MANAGE: 'org_units.manage',

  // Products & services catalogue — elevated actions
  PRODUCTS_MANAGE: 'products.manage',

  // ERP-owned accounting codes (předkontace, členění DPH, zaúčtování DPH) — link decisions
  ACCOUNTING_CODES_MANAGE: 'accounting_codes.manage',

  // Documents — elevated actions
  DOCUMENTS_REPROCESS: 'documents.reprocess',
  DOCUMENTS_DELETE: 'documents.delete',

  // Billing — self-service subscription changes (checkout, plan change, cancellation)
  BILLING_MANAGE: 'billing.manage',

  // Integration
  API_KEYS_MANAGE: 'api_keys.manage',
  WEBHOOKS_MANAGE: 'webhooks.manage',
  INTEGRATIONS_MANAGE: 'integrations.manage',

  // System-level (cross-account)
  SYSTEM_BILLING: 'system.billing',
  SYSTEM_IMPERSONATE: 'system.impersonate',
  // Publishing bridge plugin releases and moving their rollout channels. Platform-wide: a
  // release is not tenant data, and a customer admin must never be able to publish one.
  SYSTEM_PLUGIN_RELEASES: 'system.plugin_releases',
  // Reading tenants' business data from AMS, and setting the activity payload mode. Checked
  // together with an AMS operator (session or AMS API token).
  SYSTEM_TENANT_DATA: 'system.tenant_data'
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

/**
 * Every account-level permission — everything except the cross-account `system.*` set.
 */
export const ALL_ACCOUNT_PERMISSIONS: readonly Permission[] = Object.values(Permission).filter(
  p => !p.startsWith('system.')
);
